/**
 * 导出工具函数
 * 用于将小说章节导出为Word文档
 */

import { Document, Packer, Paragraph, TextRun, AlignmentType, HeadingLevel } from 'docx';
import { saveAs } from 'file-saver';

/**
 * 计算实际字数（去除换行符、空格等格式字符）
 * @param {string} content - 文本内容
 * @returns {number} 实际字数
 */
function calculateActualWordCount(content) {
  if (!content) return 0;
  return content
    .replace(/\n/g, '')      // 去除所有换行符
    .replace(/\r/g, '')      // 去除回车符
    .replace(/\t/g, '')      // 去除制表符
    .replace(/\s+/g, '')     // 去除所有空格（包括全角空格）
    .length;
}

/**
 * 导出小说章节为Word文档
 * @param {Object} params - 导出参数
 * @param {string} params.novelTitle - 小说标题
 * @param {string} params.novelOutline - 小说大纲（可选）
 * @param {Array} params.chapters - 章节数组
 * @param {string} params.author - 作者名称（可选）
 * @param {boolean} params.includeOutline - 是否包含大纲（默认true）
 * @param {boolean} params.includeStats - 是否包含统计信息（默认true）
 */
export async function exportToWord({
  novelTitle = '未命名小说',
  novelOutline = '',
  chapters = [],
  author = '作者',
  includeOutline = true,
  includeStats = true
}) {
  try {
    // 过滤出有内容的章节
    const validChapters = chapters.filter(ch => ch.content && ch.content.trim());
    
    if (validChapters.length === 0) {
      throw new Error('没有可导出的章节内容');
    }

    // 🔥 修复：计算实际总字数（去除换行符、空格等格式字符）
    const totalWords = validChapters.reduce((sum, ch) => sum + calculateActualWordCount(ch.content), 0);

    // 创建文档段落数组
    const docParagraphs = [];

    // ===== 1. 封面标题 =====
    docParagraphs.push(
      new Paragraph({
        text: novelTitle,
        heading: HeadingLevel.TITLE,
        alignment: AlignmentType.CENTER,
        spacing: { after: 400 }
      })
    );

    // 作者和导出时间
    const exportDate = new Date().toLocaleDateString('zh-CN');
    docParagraphs.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
        children: [
          new TextRun({
            text: `作者：${author}`,
            size: 24
          })
        ]
      }),
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 600 },
        children: [
          new TextRun({
            text: `导出时间：${exportDate}`,
            size: 20,
            color: '666666'
          })
        ]
      })
    );

    // 分隔线
    docParagraphs.push(
      new Paragraph({
        text: '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
        alignment: AlignmentType.CENTER,
        spacing: { after: 400 }
      })
    );

    // ===== 2. 小说大纲（可选）=====
    if (includeOutline && novelOutline && novelOutline.trim()) {
      docParagraphs.push(
        new Paragraph({
          text: '【小说大纲】',
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 }
        })
      );

      // 将大纲按段落分割
      const outlineParas = novelOutline.split('\n').filter(p => p.trim());
      outlineParas.forEach(para => {
        docParagraphs.push(
          new Paragraph({
            spacing: { after: 200 },
            children: [
              new TextRun({
                text: para,
                size: 24
              })
            ]
          })
        );
      });

      // 大纲后的分隔线
      docParagraphs.push(
        new Paragraph({
          text: '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
          alignment: AlignmentType.CENTER,
          spacing: { before: 400, after: 400 }
        })
      );
    }

    // ===== 3. 章节内容 =====
    validChapters.forEach((chapter, index) => {
      // 章节标题：确保带有章节号
      const chapterIndex = chapter.index || index + 1;
      let chapterTitle = chapter.title || '';
      
      // 如果标题没有包含章节号，需要添加
      if (!chapterTitle.includes(`第${chapterIndex}章`)) {
        chapterTitle = `第${chapterIndex}章：${chapterTitle}`;
      }
      
      docParagraphs.push(
        new Paragraph({
          text: chapterTitle,
          heading: HeadingLevel.HEADING_1,
          spacing: { before: 400, after: 300 }
        })
      );

      // 章节内容 - 按段落分割
      const contentParas = chapter.content.split('\n').filter(p => p.trim());
      contentParas.forEach(para => {
        docParagraphs.push(
          new Paragraph({
            spacing: { after: 200, line: 360 }, // 1.5倍行距
            indent: { firstLine: 480 }, // 首行缩进2字符
            children: [
              new TextRun({
                text: para,
                size: 24, // 小四字号
                font: '宋体'
              })
            ]
          })
        );
      });

      // 章节后的分隔线
      if (index < validChapters.length - 1) {
        docParagraphs.push(
          new Paragraph({
            text: '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
            alignment: AlignmentType.CENTER,
            spacing: { before: 400, after: 400 }
          })
        );
      }
    });

    // ===== 4. 统计信息（可选）=====
    if (includeStats) {
      docParagraphs.push(
        new Paragraph({
          text: '',
          spacing: { before: 600 }
        }),
        new Paragraph({
          text: '【统计信息】',
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 }
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: `总章节数：${validChapters.length}章`,
              size: 24
            })
          ],
          spacing: { after: 150 }
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: `总字数：${totalWords.toLocaleString()}字`,
              size: 24
            })
          ],
          spacing: { after: 150 }
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: `导出章节：第${validChapters[0].index || 1}章 - 第${validChapters[validChapters.length - 1].index || validChapters.length}章`,
              size: 24
            })
          ],
          spacing: { after: 150 }
        })
      );
    }

    // ===== 5. 创建Word文档 =====
    const doc = new Document({
      sections: [{
        properties: {},
        children: docParagraphs
      }]
    });

    // ===== 6. 生成并下载文件 =====
    const blob = await Packer.toBlob(doc);
    const fileName = `${novelTitle}-完整版.docx`;
    saveAs(blob, fileName);

    return {
      success: true,
      message: `成功导出 ${validChapters.length} 章，共 ${totalWords.toLocaleString()} 字`,
      fileName
    };

  } catch (error) {
    console.error('导出Word文档失败:', error);
    throw error;
  }
}

/**
 * 导出单个章节为Word文档
 * @param {Object} params - 导出参数
 * @param {string} params.novelTitle - 小说标题
 * @param {Object} params.chapter - 章节对象
 */
export async function exportSingleChapter({
  novelTitle = '未命名小说',
  chapter
}) {
  if (!chapter || !chapter.content || !chapter.content.trim()) {
    throw new Error('章节内容为空，无法导出');
  }

  return exportToWord({
    novelTitle,
    chapters: [chapter],
    includeOutline: false,
    includeStats: false
  });
}

/**
 * 导出为纯文本TXT格式
 * @param {Object} params - 导出参数
 */
export function exportToTxt({
  novelTitle = '未命名小说',
  novelOutline = '',
  chapters = [],
  author = '作者',
  includeOutline = true
}) {
  try {
    const validChapters = chapters.filter(ch => ch.content && ch.content.trim());
    
    if (validChapters.length === 0) {
      throw new Error('没有可导出的章节内容');
    }

    let txtContent = '';

    // 标题
    txtContent += `${novelTitle}\n`;
    txtContent += `作者：${author}\n`;
    txtContent += `导出时间：${new Date().toLocaleDateString('zh-CN')}\n`;
    txtContent += `${'='.repeat(50)}\n\n`;

    // 大纲
    if (includeOutline && novelOutline && novelOutline.trim()) {
      txtContent += `【小说大纲】\n${novelOutline}\n\n`;
      txtContent += `${'='.repeat(50)}\n\n`;
    }

    // 章节内容
    validChapters.forEach((chapter, index) => {
      // 章节标题：确保带有章节号
      const chapterIndex = chapter.index || index + 1;
      let chapterTitle = chapter.title || '';
      
      // 如果标题没有包含章节号，需要添加
      if (!chapterTitle.includes(`第${chapterIndex}章`)) {
        chapterTitle = `第${chapterIndex}章：${chapterTitle}`;
      }
      
      txtContent += `${chapterTitle}\n\n`;
      txtContent += `${chapter.content}\n\n`;
      
      if (index < validChapters.length - 1) {
        txtContent += `${'-'.repeat(50)}\n\n`;
      }
    });

    // 🔥 修复：统计实际字数（去除换行符、空格等格式字符）
    const totalWords = validChapters.reduce((sum, ch) => sum + calculateActualWordCount(ch.content), 0);
    txtContent += `\n${'='.repeat(50)}\n`;
    txtContent += `【统计信息】\n`;
    txtContent += `总章节数：${validChapters.length}章\n`;
    txtContent += `总字数：${totalWords.toLocaleString()}字\n`;

    // 创建Blob并下载
    const blob = new Blob([txtContent], { type: 'text/plain;charset=utf-8' });
    const fileName = `${novelTitle}-完整版.txt`;
    saveAs(blob, fileName);

    return {
      success: true,
      message: `成功导出 ${validChapters.length} 章，共 ${totalWords.toLocaleString()} 字`,
      fileName
    };

  } catch (error) {
    console.error('导出TXT文档失败:', error);
    throw error;
  }
}

